import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
import { Link, type LinkProps } from "react-router";
import { cn } from "../lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "success";
type Size = "sm" | "md";

const VARIANTS: Record<Variant, string> = {
  primary: "bg-brand text-brand-ink hover:bg-brand-hi",
  secondary: "border border-hairline bg-surface text-ink hover:border-hairline-hi hover:bg-surface-hi",
  ghost: "text-ink-soft hover:bg-surface-hi hover:text-ink",
  danger: "border border-hairline text-removed hover:bg-removed/10",
  success: "bg-passed/15 text-passed hover:bg-passed/25",
};

const SIZES: Record<Size, string> = {
  sm: "h-8 gap-1.5 rounded-md px-2.5 text-xs",
  md: "h-10 gap-2 rounded-lg px-4 text-sm",
};

export function buttonClass({ variant = "primary", size = "md", className }: { variant?: Variant; size?: Size; className?: string } = {}) {
  return cn(
    "inline-flex shrink-0 items-center justify-center font-medium transition-colors select-none",
    "outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas",
    "disabled:cursor-not-allowed disabled:opacity-50",
    VARIANTS[variant],
    SIZES[size],
    className,
  );
}

type Common = { variant?: Variant; size?: Size; className?: string };

export function Button({ variant, size, className, type = "button", ...rest }: Common & ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type={type} className={buttonClass({ variant, size, className })} {...rest} />;
}

export function ButtonLink({ variant, size, className, ...rest }: Common & LinkProps) {
  return <Link className={buttonClass({ variant, size, className })} {...rest} />;
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("rounded-xl border border-hairline bg-surface", className)}>{children}</div>;
}

export function Field({ label, hint, error, children }: { label: string; hint?: ReactNode; error?: string | null; children: ReactNode }) {
  return (
    <label className="grid gap-1.5 text-sm">
      <span className="font-medium text-ink">{label}</span>
      {children}
      {error ? <span className="text-xs text-removed">{error}</span> : hint ? <span className="text-xs text-ink-faint">{hint}</span> : null}
    </label>
  );
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn(
        "h-10 rounded-lg border border-hairline bg-canvas px-3 text-sm text-ink placeholder:text-ink-faint",
        "outline-none focus:border-brand focus:ring-2 focus:ring-brand/30",
        className,
      )}
      {...rest}
    />
  );
}

export function Toggle({ checked, onChange, label, disabled }: { checked: boolean; onChange: (value: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        "outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas",
        checked ? "bg-brand" : "bg-hairline-hi",
      )}
    >
      <span className={cn("inline-block size-5 rounded-full bg-white shadow transition-transform", checked ? "translate-x-5.5" : "translate-x-0.5")} />
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn("inline-block size-5 animate-spin rounded-full border-2 border-hairline-hi border-t-brand", className)}
    />
  );
}

export function PageLoading() {
  return (
    <div className="grid min-h-[50vh] place-items-center">
      <Spinner className="size-7" />
    </div>
  );
}

export function EmptyState({ icon, title, children }: { icon?: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="grid place-items-center gap-3 rounded-xl border border-dashed border-hairline px-6 py-14 text-center">
      {icon ? <div className="text-ink-faint">{icon}</div> : null}
      <h3 className="text-base font-semibold text-ink">{title}</h3>
      {children ? <div className="max-w-md text-sm text-ink-soft">{children}</div> : null}
    </div>
  );
}

export function ErrorMessage({ error }: { error: { message: string } }) {
  return <div className="rounded-lg border border-removed/40 bg-removed/10 px-4 py-3 text-sm text-removed">{error.message}</div>;
}

export function Avatar({ name, url, className }: { name: string; url?: string | null; className?: string }) {
  const initials = name
    .split(/\s+/)
    .map((part) => part[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return url ? (
    <img src={url} alt="" className={cn("size-8 rounded-full object-cover", className)} referrerPolicy="no-referrer" />
  ) : (
    <span className={cn("grid size-8 place-items-center rounded-full bg-brand/20 text-xs font-semibold text-brand", className)}>{initials}</span>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="rounded border border-hairline bg-surface-hi px-1.5 py-0.5 font-mono text-[10px] text-ink-soft">{children}</kbd>;
}
